import { useMemo, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Sparkle } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { computeLineDiff, type DiffLine } from "../diff/DiffEngine";

interface DiffOverlayProps {
  originalContent: string;
  proposedContent: string;
  description?: string;
  onAccept: () => void;
  onReject: () => void;
  className?: string;
}

const CONTEXT_LINES = 3;

function collapseDiffLines(lines: DiffLine[]): { lines: DiffLine[]; collapsed: boolean }[] {
  const regions: { lines: DiffLine[]; collapsed: boolean }[] = [];
  let unchangedBuffer: DiffLine[] = [];

  for (const line of lines) {
    if (line.type !== "unchanged") {
      if (unchangedBuffer.length > CONTEXT_LINES * 2 + 1) {
        regions.push({ lines: unchangedBuffer.slice(0, CONTEXT_LINES), collapsed: false });
        regions.push({
          lines: unchangedBuffer.slice(CONTEXT_LINES, -CONTEXT_LINES),
          collapsed: true,
        });
        regions.push({ lines: unchangedBuffer.slice(-CONTEXT_LINES), collapsed: false });
      } else if (unchangedBuffer.length > 0) {
        regions.push({ lines: unchangedBuffer, collapsed: false });
      }
      unchangedBuffer = [];
      const last = regions[regions.length - 1];
      if (last && !last.collapsed) {
        last.lines.push(line);
      } else {
        regions.push({ lines: [line], collapsed: false });
      }
    } else {
      unchangedBuffer.push(line);
    }
  }

  if (unchangedBuffer.length > CONTEXT_LINES) {
    regions.push({ lines: unchangedBuffer.slice(0, CONTEXT_LINES), collapsed: false });
    if (unchangedBuffer.length > CONTEXT_LINES + 1) {
      regions.push({ lines: unchangedBuffer.slice(CONTEXT_LINES), collapsed: true });
    }
  } else if (unchangedBuffer.length > 0) {
    regions.push({ lines: unchangedBuffer, collapsed: false });
  }

  return regions;
}

function DiffLineRow({ line }: { line: DiffLine }) {
  const lineNum = line.type === "remove" ? line.oldLineNumber : line.newLineNumber;

  return (
    <div
      className={cn(
        "flex items-stretch font-mono text-[12px] leading-[20px] min-h-[20px]",
        line.type === "add" && "bg-[var(--color-signal-green)]/8",
        line.type === "remove" && "bg-[var(--color-soft-red)]/8",
      )}
    >
      <span
        className={cn(
          "shrink-0 w-[44px] text-right pr-2 select-none text-[10px] leading-[20px]",
          "border-r border-[var(--border-hairline)]",
          line.type === "unchanged" && "text-[var(--text-muted)]",
          line.type === "add" && "text-[var(--color-signal-green)] bg-[var(--color-signal-green)]/5",
          line.type === "remove" && "text-[var(--color-soft-red)] bg-[var(--color-soft-red)]/5",
        )}
      >
        {lineNum ?? ""}
      </span>
      <span
        className={cn(
          "shrink-0 w-[20px] text-center select-none text-[11px] leading-[20px] font-semibold",
          line.type === "unchanged" && "text-[var(--text-muted)]",
          line.type === "add" && "text-[var(--color-signal-green)]",
          line.type === "remove" && "text-[var(--color-soft-red)]",
        )}
      >
        {line.type === "add" ? "+" : line.type === "remove" ? "−" : " "}
      </span>
      <span
        className={cn(
          "flex-1 px-2 whitespace-pre-wrap break-all",
          line.type === "unchanged" && "text-[var(--text-secondary)]",
          line.type === "add" && "text-[var(--text-primary)]",
          line.type === "remove" && "text-[var(--text-muted)] line-through decoration-[var(--color-soft-red)]/40",
        )}
      >
        {line.content || "\u00A0"}
      </span>
    </div>
  );
}

export function DiffOverlay({
  originalContent,
  proposedContent,
  description,
  onAccept,
  onReject,
  className,
}: DiffOverlayProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const firstChangeRef = useRef<HTMLDivElement>(null);

  const diff = useMemo(
    () => computeLineDiff(originalContent, proposedContent),
    [originalContent, proposedContent],
  );

  const regions = useMemo(() => collapseDiffLines(diff.lines), [diff.lines]);

  useEffect(() => {
    const timer = setTimeout(() => {
      firstChangeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className={cn(
          "absolute inset-0 z-40 flex flex-col",
          "bg-[var(--bg-base)]",
          className,
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)]">
          <div className="flex items-center gap-2">
            <Sparkle size={12} weight="fill" className="text-[var(--color-lever-orange)]" />
            <span className="text-[11px] font-semibold text-[var(--text-primary)]">
              AI Suggested Changes
            </span>
            {description && (
              <>
                <span className="text-[var(--text-muted)]">·</span>
                <span className="text-[10px] text-[var(--text-muted)]">{description}</span>
              </>
            )}
            <span className="text-[10px] text-[var(--text-muted)] ml-1">
              <span className="text-[var(--color-signal-green)]">+{diff.stats.additions}</span>
              {" "}
              <span className="text-[var(--color-soft-red)]">−{diff.stats.deletions}</span>
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.95 }}
              onClick={onAccept}
              className={cn(
                "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1 text-[11px] font-semibold",
                "bg-[var(--color-signal-green)] text-white",
                "hover:brightness-110 transition-all duration-100",
              )}
            >
              <Check size={11} weight="bold" />
              Accept
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.95 }}
              onClick={onReject}
              className={cn(
                "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1 text-[11px] font-medium",
                "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
                "border border-[var(--border-hairline)] hover:bg-[var(--bg-surface-hover)]",
                "transition-colors duration-100",
              )}
            >
              <X size={11} weight="bold" />
              Discard
            </motion.button>
          </div>
        </div>

        {/* Diff content */}
        <div ref={scrollRef} className="flex-1 overflow-auto">
          {regions.map((region, rIdx) => {
            if (region.collapsed) {
              return (
                <div
                  key={`r-${rIdx}`}
                  className="flex items-center gap-2 px-3 py-1 text-[10px] text-[var(--text-muted)] bg-[var(--bg-surface)] border-y border-[var(--border-hairline)]"
                >
                  ··· {region.lines.length} unchanged lines ···
                </div>
              );
            }
            let foundFirstChange = false;
            return (
              <div key={`r-${rIdx}`}>
                {region.lines.map((line, lIdx) => {
                  const isChange = line.type !== "unchanged";
                  let ref: React.RefObject<HTMLDivElement | null> | undefined;
                  if (isChange && !foundFirstChange) {
                    foundFirstChange = true;
                    ref = firstChangeRef;
                  }
                  return (
                    <div key={`${rIdx}-${lIdx}`} ref={ref}>
                      <DiffLineRow line={line} />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
